export type FileLifecycleStatus =
  | "PENDING"
  | "CREATED"
  | "INITIATED"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "REJECTED";

export type FileContentStatus = "CREATED" | "COMPLETED" | "FAILED";

export const FILE_TERMINAL_STATUSES: FileLifecycleStatus[] = ["COMPLETED", "FAILED", "REJECTED"];

export const FILE_NON_TERMINAL_STATUSES: FileLifecycleStatus[] = ["PENDING", "CREATED", "INITIATED", "PROCESSING"];

export const isFileTerminal = (status?: string | null): boolean => {
  return Boolean(status) && FILE_TERMINAL_STATUSES.includes(status as FileLifecycleStatus);
};

export interface BulkUploadFileAttributes {
  total_rows?: number | null;
  processed_rows?: number | null;
  key?: string | null;
  bucket_name?: string | null;
  date?: string | null;
  e_tag?: string | null;
  original_filename?: string | null;
  actor_id?: string | null;
  actor_role?: string | null;
  actor_organisation_id?: string | null;
  actor_name?: string | null;
  actor_organisation_name?: string | null;
}

export interface BulkUploadFile {
  file_id: string;
  file_type: string;
  process_status: FileLifecycleStatus;
  version?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  attributes: BulkUploadFileAttributes;
}

export interface CashTransactionAttrs {
  wallet_id?: string;
  wallet_name?: string | null;
  wallet_display_id?: string | null;
  amount?: number;
  merchantName?: string;
  transaction_user_id?: string;
  transaction_user_name?: string | null;
  link_id?: string;
  owner_id?: string;
  row_num?: number;
  actor_id?: string;
  actor_role?: string;
  actor_organisation_id?: string;
}

export interface DepositAttrs {
  wallet_id?: string;
  wallet_name?: string | null;
  wallet_display_id?: string | null;
  amount?: number;
  balance_before_deposit?: number;
  balance_after_deposit?: number;
  wallet_before_deposit?: number;
  row_num?: number;
  actor_id?: string;
  actor_role?: string;
  actor_organisation_id?: string;
}

export interface NovattiAttrs {
  settlement_status?: string;
  ppan?: string;
  approval_code?: string;
  novatti_amount?: number;
  spendable_amount?: number;
  row_num?: number;
}

export type FileContentAttributes = CashTransactionAttrs | DepositAttrs | NovattiAttrs | Record<string, unknown>;

export interface BulkUploadFileContent {
  content_id: string;
  file_id: string;
  file_type: string;
  process_status: FileContentStatus;
  link_id?: string | null;
  link_type?: string | null;
  data?: string | null;
  err_message?: string | null;
  attributes: FileContentAttributes;
  content?: Record<string, unknown> | null;
}

export interface BulkUploadFileListing {
  list: BulkUploadFile[];
  total: number;
}

export interface BulkUploadFileContentListing {
  list: BulkUploadFileContent[];
  total: number;
}

export interface BulkUploadListingFilters {
  fileTypes: string[];
  processStatuses: string[];
  startDate: string | null;
  endDate: string | null;
  searchText: string;
}

export interface BulkUploadDetailFilters {
  processStatuses: string[];
  searchText: string;
}

export const parseFileContentAttributes = (raw: unknown): FileContentAttributes => {
  if (!raw) return {};
  if (typeof raw === "object") return raw as FileContentAttributes;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as FileContentAttributes;
    } catch {
      return {};
    }
  }
  return {};
};

export const parseFileContentContent = (raw: unknown): Record<string, unknown> => {
  if (!raw) return {};
  if (typeof raw === "object") return raw as Record<string, unknown>;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
};
